import React, { useState, useEffect } from 'react';
import '../../spinner.css';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { getUserDetails } from '../../services/UserService';
import { getUserReport, getAssignedCoursesData, getUserCoursesAndModulesAndLearningsData, getUserLearningTimeAndScoreData } from '../../services/EvaluationService';
import { Chart, ArcElement, Tooltip, Legend, Title } from 'chart.js';
import { userDefaultPicture } from '../../utility/constants';
import { Doughnut } from 'react-chartjs-2';
import Loader from '../../utility/Loader';
import { downloadUserReportPdf, downloadLearnerReportMatrixPdf } from '../../utility/downloadReport';

Chart.register(ArcElement, Tooltip, Legend, Title);

export default function UserReport() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [user, setUser] = useState({});
    const [learnings, setLearnings] = useState({ recentlyCompleted: {}, ongoing: {}, upcoming: {}, courses: [] });
    const [colors, setColors] = useState(['Red', 'Blue', 'Yellow', 'Green', 'Pink', 'Orange', 'Grey', 'Violet', 'Turquoise']);
    const [certificates, setCertificates] = useState("");
    const [avgPoScore, setAvgPoScore] = useState(0);
    const [courseReport, setCourseReport] = useState({ course_completed: 0, modules_completed: 0, tests_completed: 0, min_spent: 0 });
    const [courseDetailedReport, setCourseDetailedReport] = useState([]);
    const [data, setData] = useState({
        labels: [],
        datasets: [{
            label: 'Dataset',
            data: [],
            backgroundColor: [],
            hoverOffset: 4
        }]
    });

    const options = {
        plugins: {
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const index = context.dataIndex;
                        let course_name = courseDetailedReport[index].course_name;
                        let modules_completed = courseDetailedReport[index].modules_completed;
                        let pr_completed = courseDetailedReport[index].pr_completed;
                        let po_completed = courseDetailedReport[index].po_completed;
                        let total_learning_time = courseDetailedReport[index].mins_spent;
                        return [course_name, modules_completed + " Modules Completed", pr_completed + " Pre-Learning Assessments", po_completed + " Post-Learning Assessments", total_learning_time + " Minutes Spent"];
                    },
                    title: function () {
                        return 'Detailed Data';
                    }
                },
                useHTML: true
            }
        }
    };

    useEffect(() => {
        const userDetails = getUserDetails();
        if (userDetails.token === undefined) {
            navigate('/login');
        } else {
            let userReport = sessionStorage.getItem('userReport') !== undefined ? JSON.parse(sessionStorage.getItem('userReport')) : {};
            if (userReport !== null && userReport !== undefined) {
                setUser(userReport);
                sessionStorage.removeItem('userReport');
                getAllData(userDetails, userReport);
                getAssignedCourses(userReport);

            } else {
                navigate('/admin/learnerreport');
            }

        }


        Chart.defaults.plugins.tooltip.backgroundColor = 'rgb(0, 0, 156)';
        Chart.defaults.plugins.legend.position = 'bottom';
        Chart.defaults.plugins.legend.title.display = true;
        Chart.defaults.plugins.legend.title.text = 'User Data';
        Chart.defaults.plugins.legend.title.font = 'Helvetica Neue';
    }, []);

    const getAllData = async (userDetails, userReport) => {
        setLoading(true);
        await getUserReportData(userDetails.token, userReport.user_id);
        setLoading(false);
    };

    const getUserReportData = async (token, user_id) => {
        let resp = await getUserReport(token, user_id);
        if (resp.error === false) {
            let reportData = resp.data.score_result !== undefined && resp.data.score_result !== null ? resp.data.score_result : [];
            let dataToSet = "";
            let count = 0;
            let score = 0;
            for (let i = 0; i < reportData.length; i++) {
                
                score = score + (reportData[i].po_score !== undefined && reportData[i].po_score !== null ? reportData[i].po_score : 0);
                if((reportData[i].po_score !== undefined && reportData[i].po_score !== null ? reportData[i].po_score : 0)>=60){
                    if (dataToSet !== "") {
                        dataToSet = dataToSet + " | ";
                    }
                    dataToSet = dataToSet + reportData[i].course_name;
                }
                count = count + 1;
            }
            if (count > 0) {
                setAvgPoScore(Math.ceil(score / count));
            }
            setCertificates(dataToSet);
            let courseData = resp.data.course_result !== undefined && resp.data.course_result !== null ? resp.data.course_result : [];
            let modules_completed = 0;
            let course_completed = 0;
            let tests_completed = 0;
            let learning_secs = 0;
            let labels = [];
            let dataValues = [];
            let bgColors = [];

            let newData = {
                labels: labels,
                datasets: [{
                    label: 'Dataset',
                    data: dataValues,
                    backgroundColor: bgColors,
                    hoverOffset: 4
                }]
            };

            for (let i = 0; i < courseData.length; i++) {
                courseData[i].learning_sec = parseInt(courseData[i].learning_sec);
                courseData[i].modules_completed = parseInt(courseData[i].modules_completed);
                courseData[i].num_modules = parseInt(courseData[i].num_modules);
                courseData[i].po_modules = parseInt(courseData[i].po_modules);
                courseData[i].pr_modules = parseInt(courseData[i].pr_modules);
                courseData[i].po_completed = parseInt(courseData[i].po_modules);
                courseData[i].pr_completed = parseInt(courseData[i].pr_modules);
                courseData[i].mins_spent = Math.ceil(courseData[i].learning_sec / 60);

                labels.push(courseData[i].course_name);
                dataValues.push(courseData[i].mins_spent);
                bgColors.push(colors[i]);

                if (courseData[i].modules_completed + courseData[i].po_modules + courseData[i].pr_modules === courseData[i].num_modules) {
                    course_completed = course_completed + 1;
                }
                modules_completed = modules_completed + courseData[i].modules_completed;
                tests_completed = tests_completed + courseData[i].po_modules + courseData[i].pr_modules;
                learning_secs = learning_secs + courseData[i].learning_sec;
            }
            setCourseDetailedReport(courseData)
            setCourseReport({ course_completed: course_completed, modules_completed: modules_completed, tests_completed: tests_completed, min_spent: Math.ceil(learning_secs / 60) });
            setData(newData);

        } else {
            toast.error(resp.data);
        }
    };

    const downloadPdfReport = async () => {
        if (learnings.courses.length === 0) {
            toast.error('No data available to download');
            return;
        }
        setLoading(true);
        try {
            // Generate filename with user name and timestamp
            const timestamp = new Date().toISOString().split('T')[0];
            const userName = user.name ? user.name.replace(/[^a-zA-Z0-9]/g, '-') : 'user';
            const filename = `learner-report-${userName}-${timestamp}.pdf`;
            
            await downloadLearnerReportMatrixPdf(
                {
                    user: user,
                    certifications: (certificates || "").split("|"),
                    courses: learnings.courses,
                    filename: filename
                },
            );
            
            toast.success('User report downloaded successfully');
        } catch (error) {
            console.error('Error downloading PDF:', error);
            toast.error('Failed to download report. Please try again.');
        }
        setLoading(false);
    };

    const handleDownloadReport = async () => {
        if (courseDetailedReport.length === 0) {
            toast.error('No data available to download');
            return;
        }
        
        try {
            // Generate filename with user name and timestamp
            const timestamp = new Date().toISOString().split('T')[0];
            const userName = user.name ? user.name.replace(/[^a-zA-Z0-9]/g, '-') : 'user';
            const filename = `user-report-${userName}-${timestamp}.pdf`;
            
            await downloadUserReportPdf(
                user,
                courseDetailedReport,
                courseReport,
                avgPoScore,
                certificates,
                filename
            );
            
            toast.success('User report downloaded successfully');
        } catch (error) {
            console.error('Error downloading PDF:', error);
            toast.error('Failed to download report. Please try again.');
        }
    };

    const getAssignedCourses = async (userReport) => {
        const userDetails = getUserDetails();
        setLoading(true);
        const assignedCourses = await getAssignedCoursesData(userDetails.token, userReport);
        if (assignedCourses.error) {
          toast.error(assignedCourses.data);
        } else {
          let course_ids = [];
          for (let i = 0; i < assignedCourses.data.length; i++) {
            course_ids.push(assignedCourses.data[i].course_id);
          }
          await getUserCoursesAndModulesAndLearnings(userReport, course_ids);
        }
        setLoading(false);
    };

    const getUserCoursesAndModulesAndLearnings = async (userReport, course_ids) => {
        const userDetails = getUserDetails();
        const assignedCourses = await getUserCoursesAndModulesAndLearningsData(userDetails.token, userReport, course_ids.join(','));
        const learningTimeObj = await getUserLearningTimeAndScoreData(userDetails.token, userReport);
        let learningTime = []
        if(learningTimeObj.error === false){
            learningTime = learningTimeObj.data;
        }
        if (assignedCourses.error) {
          toast.error(assignedCourses.data);
          setLearnings({ recentlyCompleted: {}, ongoing: {}, upcoming: {}, courses: [] });
        } else {
          let data = assignedCourses.data;
          data.courses = groupModuleByCourse(assignedCourses.data.courses, course_ids, learningTime);
          let overviewObj = { assessmentCompleted: 0, totalCompleted: 0, score: 0, totalModules: 0, totalCourse: 0, totalStarted: 0 };
          let firstCourseObj = { course_name: "", module_seq_no: 0, module_name: "", module_code: "" };
          for (let i = 0; i < data.courses.length; i++) {
            if (i == 0) {
              firstCourseObj.course_name = data.courses[i].course_name;
            }
            data.courses[i]["completed"] = 0;
            overviewObj.totalCourse = overviewObj.totalCourse + 1;
            for (let j = 0; j < data.courses[i].modules.length; j++) {
              if (i == 0 && j == 0) {
                firstCourseObj.module_seq_no = data.courses[i].modules[j].module_seq_no;
                firstCourseObj.module_name = data.courses[i].modules[j].module_name;
                firstCourseObj.module_code = data.courses[i].modules[j].module_code;
    
              }
              overviewObj.totalModules = overviewObj.totalModules + 1;
              if (data.courses[i].modules[j].status === 'COMPLETED') {
                data.courses[i]["completed"] = data.courses[i]["completed"] + 1;
                overviewObj.totalCompleted = overviewObj.totalCompleted + 1;
              }
              if (data.courses[i].modules[j].status === 'STARTED') {
                overviewObj.totalStarted = overviewObj.totalStarted + 1;
              }
              if (data.courses[i].modules[j].status === 'COMPLETED' && data.courses[i].modules[j].module_type === 'ASSESSMENT') {
                overviewObj.assessmentCompleted = overviewObj.assessmentCompleted + 1;
              }
            }
          }
          overviewObj.score = Math.ceil(overviewObj.totalCompleted * 100 / overviewObj.totalModules);
          setLearnings(data);
        }
    };

    const groupModuleByCourse = (data, course_ids, learningTime) => {
        let courseMap = {};
        let learningMap = {};
        for (let i = 0; i < learningTime.length; i++) {
            const item = learningTime[i];
            const courseId = parseInt(item.course_id);
          
            if (learningMap[courseId] === undefined) {
              learningMap[courseId] = { po_score: 0, learning_sec: 0 };
            }
          
            if (item.metric === "po_score") {
              learningMap[courseId].po_score = item.num ?? 0; // handle undefined/null
            } else if (item.metric === "learning_sec") {
              learningMap[courseId].learning_sec = item.num ?? 0;
            }
        }
        for (let i = 0; i < data.length; i++) {
          let courseAndModule = Object.assign(data[i]);
          const courseId = courseAndModule.course_id;

          if (!courseMap[courseId]) {
            const learningData = learningMap[courseId] || {}; // fallback if missing
        
            courseMap[courseId] = {
              course_id: courseAndModule.course_id,
              course_name: courseAndModule.course_name,
              course_code: courseAndModule.course_code,
              course_desc: courseAndModule.course_desc,
              color: courseAndModule.color,
              modules: [],
              po_score: learningData.po_score ?? 0,
              learning_sec: learningData.learning_sec ?? 0,
            };
          }

          courseMap[courseAndModule.course_id]["modules"].push({
            module_id: courseAndModule.module_id,
            pedagogy_count: courseAndModule.pedagogy_count,
            module_code: courseAndModule.module_code,
            module_name: courseAndModule.module_name,
            module_desc: courseAndModule.module_desc,
            module_type: courseAndModule.module_type,
            module_seq_no: courseAndModule.module_seq_no,
            learning_pedagogy_count: courseAndModule.learning_pedagogy_count === null || courseAndModule.learning_pedagogy_count === undefined ? 0 : courseAndModule.learning_pedagogy_count,
            status: courseAndModule.status,
          });
        }
        let courseList = Object.values(courseMap);
        const mappedData = course_ids.map(id => courseList.find(item => parseInt(item.course_id) === parseInt(id)));
        const sortedData = mappedData.filter(item => item);
        return sortedData;
    };

    return (
        <div>
            {loading ? (
                <Loader />
            ) : (
                <>
                    <div className='pt-10 lg:px-12 px-6 pb-12'>
                        <h3 className='text-2xl text-secondary text-center inter-700 lg:hidden mb-4'>User Report</h3>
                        <div className='back-btn flex justify-start mb-5 md:mb-8'>
                            <a onClick={() => navigate('/admin/learnerreport')} className='cursor-pointer w-auto bg-tertiary hover:bg-black flex items-center justify-center px-6 h-7 2xl:h-8 text-white inter-500 text-xs 2xl:text-lg rounded-3xl lg:rounded-full transition duration-300 button-shadow'>Back</a>
                        </div>
                        <div className='report-prf-row flex items-center flex-wrap  mb-10'>
                            <div className='report-prf-icon w-full md:w-1/12'>
                                <img className='rounded-image h-20 w-20 object-cover' src={user.image_link === undefined || user.image_link === null ? userDefaultPicture : user.image_link} alt='' />
                            </div>
                            <div className='report-prf-dtl w-full md:w-11/12 md:pl-10 mt-4 md:mt-0'>
                                <h3 className='text-2xl text-primary inter-700'>{user.name}</h3>
                                <p className='text-base text-[#A30D11] inter-500'>{user.cohort_name}</p>
                            </div>
                        </div>
                        <div className='report-crtfd flex mb-7'>
                            <div className='md:w-1/12'></div>
                            <div className='w-full md:w-11/12 md:pl-10'>
                                <div className='flex items-center justify-between flex-wrap'>
                                    <h3 className='text-2xl text-tertiary inter-700'>Certificates Earned</h3>
                                    <a onClick={downloadPdfReport} className='cursor-pointer bg-primary hover:bg-black flex items-center justify-center px-6 h-10 2xl:h-12 text-white inter-600 text-base md:text-sm lg:text-base 2xl:text-lg rounded-3xl lg:rounded-full transition duration-300 button-shadow'>Download PDF</a>
                                </div>
                                <p className='text-lg text-[#DF0000] inter-500'>{certificates}</p>
                            </div>
                        </div>
                        <div className='report-card-row flex'>
                            <div className='md:w-1/12'></div>
                            <div className='w-full md:w-11/12 md:pl-10'>
                                <div className='cohort-big-card rounded-3xl lg:p-10 p-8 mb-8'>
                                    <h2 className='text-3xl text-white inter-700 mb-4'>Summary</h2>
                                    <div className='flex items-center flex-wrap'>
                                        <div className='w-full md:w-1/2'>
                                            <div className='cohort-big-card-left'>
                                                <div className='mb-3 md:mb-6'>
                                                    <h3 className='text-2xl text-white inter-700'>{Math.ceil(avgPoScore)}%</h3>
                                                    <p className='text-base 2xl:text-lg text-white inter-400'>Avg Post-Learning Assessment Score</p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className='w-full md:w-1/2 mt-3 md:mt-0'>
                                            <div className='cohort-big-card-right'>
                                                <p className='text-xl text-white inter-400 mb-3'><span className='inter-700'>{courseReport.course_completed}</span> Technology Completed </p>
                                                <p className='text-xl text-white inter-400 mb-3'><span className='inter-700'>{courseReport.modules_completed}</span> Modules Completed </p>
                                                <p className='text-xl text-white inter-400 mb-3'><span className='inter-700'>{courseReport.tests_completed}</span> Tests Completed </p>
                                                <p className='text-xl text-white inter-400 mb-0'><span className='inter-700'>{courseReport.min_spent}</span> Minutes Spent </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className='report-graph flex justify-center'>
                                    <div className='w-full lg:w-2/5 bg-[#F1EAEA] rounded-3xl p-6'>
                                        <Doughnut data={data} options={options} />
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </>
            )
            }
        </div>
    );
}